import React, { useMemo, useCallback, useEffect, useState } from "react";
import Autocomplete from "@mui/material/Autocomplete";
import TextField from "@mui/material/TextField";

import { Textfield, SingleSelect, MultipleSelect, ErrorMessage } from "widgets";
import { useFetchAgentsByUserIds, useFetchPostalCodeDetails } from "store/backend/apiHooks";
import { DEBOUNCE_TIME_MS } from "constants/constants";
import {
  INDIA_POSTAL_CODE_LENGTH,
  MAX_VISIBLE_BOARD_CHIPS,
  SCHOOL_BOARD_FIELDS,
  SCHOOL_TYPE_OPTIONS,
  STATE_DISTRICT_DISPLAY_FIELDS,
  sanitizeUdiseCode,
} from "constants/schoolConstants";
import {
  clearStateDistrictFormFields,
  filterCityOptionsForCountry,
  getAgentNameSearchTerm,
  getSelectFieldErrorState,
  getSelectedCountryOption,
  idsMatch,
  isIndiaCountry,
  isUaeCountry,
  resetSchoolAddressForCountryChange,
  useSchoolExecutiveIdAutocomplete,
  useSchoolPocAutocomplete,
} from "helpers/schoolUtils";
import { shouldShowFormError, useDebouncedCallback } from "utils/utils";
import Loader from "shared/Icons/Loader/Loader";
import GradeWiseFields from "./GradeWiseFields";

import formStyles from "../TenantForm/TenantForm.module.scss";
import styles from "./SchoolForm.module.scss";

const SelectErrors = ({ state, yupMessage, fetchMessage }) => (
  <>
    {state.yupError ? <ErrorMessage>{yupMessage}</ErrorMessage> : null}
    {state.fetchError ? <ErrorMessage>{fetchMessage}</ErrorMessage> : null}
  </>
);

const FormField = ({ children, error, showErr, fullWidth }) => (
  <div className={`${formStyles.field} ${fullWidth ? formStyles.fieldFull : ""}`}>
    {children}
    {showErr && error ? <ErrorMessage>{error}</ErrorMessage> : null}
  </div>
);

const SchoolAgentAutocompleteField = ({
  fieldName,
  label,
  selectedId,
  error,
  showErr,
  onBlur,
  setFieldValue,
  setFieldTouched,
  useAutocomplete,
  assignedAgentsResponse,
}) => {
  const [inputValue, setInputValue] = useState("");
  const [searchTerm, setSearchTerm] = useState("");
  const debouncedSetSearchTerm = useDebouncedCallback(
    (nextSearchTerm) => {
      setSearchTerm(nextSearchTerm);
    },
    DEBOUNCE_TIME_MS,
  );
  const {
    options,
    selectedOption,
    isSearching,
    minSearchLength,
  } = useAutocomplete(selectedId, searchTerm, assignedAgentsResponse);
  const selectedLabel = selectedOption ? selectedOption.label : "";
  const typedName = getAgentNameSearchTerm(inputValue);
  const displayInput =
    selectedLabel &&
    (!typedName || typedName === getAgentNameSearchTerm(selectedLabel))
      ? selectedLabel
      : inputValue;
  const handleSelect = useCallback(
    (agentUamId) => {
      setFieldValue(fieldName, agentUamId, true);
      setFieldTouched(fieldName, true, false);
    },
    [fieldName, setFieldValue, setFieldTouched],
  );
  return (
    <FormField error={error} showErr={showErr}>
      <Autocomplete
        className={styles.autocompleteField}
        options={options}
        loading={isSearching}
        value={selectedOption}
        inputValue={displayInput}
        onInputChange={(_, newInputValue, reason) => {
          if (reason === "clear") {
            debouncedSetSearchTerm.cancel();
            setInputValue("");
            setSearchTerm("");
            return;
          }
          if (reason !== "input") return;
          const agentNameSearchTerm = getAgentNameSearchTerm(newInputValue);
          setInputValue(agentNameSearchTerm);
          debouncedSetSearchTerm(agentNameSearchTerm);
        }}
        onChange={(_, newValue) => {
          if (!newValue) {
            debouncedSetSearchTerm.cancel();
            handleSelect("");
            setInputValue("");
            setSearchTerm("");
            return;
          }
          const selectedAgentName = getAgentNameSearchTerm(newValue.label);
          debouncedSetSearchTerm.cancel();
          setInputValue(selectedAgentName);
          setSearchTerm(selectedAgentName);
          handleSelect(newValue.value);
        }}
        onBlur={onBlur}
        getOptionLabel={(option) => {
          if (!option) return "";
          if (!option.label) return "";
          return option.label;
        }}
        isOptionEqualToValue={(option, selected) => {
          if (!selected) return false;
          return idsMatch(option.value, selected.value);
        }}
        noOptionsText={
          typedName.length < minSearchLength
            ? `Enter at least ${minSearchLength} characters`
            : "No options"
        }
        renderInput={(params) => (
          <TextField {...params} label={label} variant="outlined" fullWidth />
        )}
      />
    </FormField>
  );
};

const SearchableSelect = ({
  tag,
  options,
  value,
  onChange,
  onBlur,
  disabled,
  getOptionDisabled,
}) => {
  const selectedOption =
    options.find((option) => idsMatch(option.value, value)) || null;
  return (
    <Autocomplete
      className={styles.autocompleteField}
      options={options}
      value={selectedOption}
      disabled={disabled}
      getOptionLabel={(option) => option.label || ""}
      isOptionEqualToValue={(option, selected) =>
        idsMatch(option.value, selected.value)
      }
      getOptionDisabled={getOptionDisabled}
      onChange={(_, newValue) => onChange(newValue?.value ?? "")}
      onBlur={onBlur}
      renderInput={(params) => (
        <TextField {...params} label={tag} variant="outlined" fullWidth />
      )}
    />
  );
};

const SchoolForm = ({
  mapping,
  values,
  errors,
  touched,
  setFieldValue,
  setFieldTouched = () => {},
  setFieldError = () => {},
  handleBlur = () => {},
  submitCount = 0,
  udiseCodeDisabled = false,
  schoolNameDisabled = false,
  assignedAgentUserIds,
}) => {
  const {
    countryOptions,
    allCityOptions,
    boardOptions,
    gradeOptions,
    tierOptions,
    isCountryListLoading,
    isCityListLoading,
    isBoardListLoading,
    isGradeListLoading,
    isTierListLoading,
    isErrorOnCountryList,
    isErrorOnCityList,
    isErrorOnBoardList,
    isErrorOnGradeList,
    isErrorOnTierList,
    errorMessageOnFetchCountryList,
    errorMessageOnFetchCityList,
    errorMessageOnFetchBoardList,
    errorMessageOnFetchGradeList,
    errorMessageOnFetchTierList,
  } = mapping;
  const submitAttempted = submitCount > 0;
  const showError = (fieldTouched, fieldError) =>
    shouldShowFormError(fieldTouched, fieldError, submitAttempted);
  const touch = (field) => () => setFieldTouched(field, true, false);
  const setAndTouch = (field) => (nextValue) => {
    setFieldValue(field, nextValue, true);
    setFieldTouched(field, true, false);
  };
  const selectedCountry = getSelectedCountryOption(
    countryOptions,
    values.countryId,
  );
  const hasValidCountrySelected = !!selectedCountry;
  const isIndia = isIndiaCountry(selectedCountry?.label);
  const isAreaRequired = hasValidCountrySelected && !isIndia;
  const isAreaDisabled = !hasValidCountrySelected || isIndia;
  const isStateDistrictReadOnly = isIndia || isUaeCountry(selectedCountry?.label) || !hasValidCountrySelected;
  const cityOptions = useMemo(
    () =>
      filterCityOptionsForCountry(
        allCityOptions,
        hasValidCountrySelected ? values.countryId : "",
        values.cityId,
      ),
    [allCityOptions, hasValidCountrySelected, values.countryId, values.cityId],
  );
  const pin = String(values.postalCode || "");
  const { data: assignedAgentsResponse } = useFetchAgentsByUserIds(
    assignedAgentUserIds,
  );
  const {
    data: postalCodeDetails,
    isFetching: isPostalLookupPending,
    isPending: isPostalLoading,
    isError: isPostalError,
  } = useFetchPostalCodeDetails(
    isIndia && pin.length === INDIA_POSTAL_CODE_LENGTH ? pin : undefined,
  );
  useEffect(() => {
    if (!isIndia) return;
    const pinStr = String(values.postalCode || "");
    if (
      pinStr.length === INDIA_POSTAL_CODE_LENGTH &&
      postalCodeDetails?.data?.district
    ) {
      const { district, state } = postalCodeDetails.data;
      setFieldValue("stateId", Number(state.id), false);
      setFieldValue("state", state.name, false);
      setFieldValue("districtId", Number(district.id), false);
      setFieldValue("district", district.name, false);
      setFieldError("postalCode", undefined);
    } else if (
      (pinStr.length > 0 && pinStr.length < INDIA_POSTAL_CODE_LENGTH) ||
      (pinStr.length === INDIA_POSTAL_CODE_LENGTH && isPostalError)
    ) {
      clearStateDistrictFormFields(setFieldValue);
    }
  }, [
    postalCodeDetails,
    values.postalCode,
    isIndia,
    isPostalError,
    setFieldValue,
    setFieldError,
  ]);
  const isInvalidPincode =
    isIndia &&
    pin.length === INDIA_POSTAL_CODE_LENGTH &&
    !isPostalLookupPending &&
    !isPostalLoading &&
    (isPostalError || !postalCodeDetails?.data?.district);
  const selectErrors = (field, isFetchError) =>
    getSelectFieldErrorState({
      touched,
      errors,
      field,
      isFetchError,
      submitAttempted,
    });
  const fetchMessage = (error, fallback) => error?.message || fallback;
  const handleCountryChange = (newValue) => {
    if (!idsMatch(values.countryId, newValue)) {
      resetSchoolAddressForCountryChange({
        newCountryId: newValue,
        countryOptions,
        setFieldValue,
        setFieldTouched,
        setFieldError,
      });
      setFieldTouched("udiseCode", false, false);
      setFieldError("udiseCode", undefined);
    }
    setAndTouch("countryId")(newValue);
  };
  const handleCityChange = (newValue) => {
    setAndTouch("cityId")(newValue);
    if (!newValue) {
      setFieldValue("tierId", "", false);
      return;
    }
    const selectedCity = allCityOptions.find((city) =>
      idsMatch(city.value, newValue),
    );
    if (selectedCity?.tierId != null) {
      setFieldValue("tierId", selectedCity.tierId, false);
    }
  };
  return (
    <div className={formStyles.formRoot}>
      <section className={formStyles.section}>
        <h2 className={formStyles.sectionHeading}>School information</h2>
        <div className={formStyles.formGrid}>
          <FormField
            error={errors.schoolName}
            showErr={showError(touched.schoolName, errors.schoolName)}
          >
            <Textfield
              name="schoolName"
              label="School name *"
              value={values.schoolName}
              handleOnChange={(event) =>
                setAndTouch("schoolName")(event.target.value)
              }
              onBlur={handleBlur}
              disabled={schoolNameDisabled}
            />
          </FormField>
          {isIndia ? (
            <FormField
              error={errors.udiseCode}
              showErr={showError(touched.udiseCode, errors.udiseCode)}
            >
              <Textfield
                name="udiseCode"
                label="UDISE code *"
                value={values.udiseCode}
                handleOnChange={(event) =>
                  setAndTouch("udiseCode")(sanitizeUdiseCode(event.target.value))
                }
                onBlur={handleBlur}
                disabled={udiseCodeDisabled}
              />
            </FormField>
          ) : null}
          <FormField
            error={errors.schoolType}
            showErr={showError(touched.schoolType, errors.schoolType)}
          >
            <SingleSelect
              name="schoolType"
              tag="School type *"
              menuItems={SCHOOL_TYPE_OPTIONS}
              value={values.schoolType}
              onBlur={handleBlur("schoolType")}
              handleChange={(e) => setAndTouch("schoolType")(e.target.value)}
            />
          </FormField>
          <SchoolAgentAutocompleteField
            fieldName="pocId"
            label="School POC *"
            selectedId={values.pocId}
            error={errors.pocId}
            showErr={showError(touched.pocId, errors.pocId)}
            onBlur={touch("pocId")}
            setFieldValue={setFieldValue}
            setFieldTouched={setFieldTouched}
            useAutocomplete={useSchoolPocAutocomplete}
            assignedAgentsResponse={assignedAgentsResponse}
          />
          <SchoolAgentAutocompleteField
            fieldName="executiveId"
            label="Genius Execution Rep *"
            selectedId={values.executiveId}
            error={errors.executiveId}
            showErr={showError(touched.executiveId, errors.executiveId)}
            onBlur={touch("executiveId")}
            setFieldValue={setFieldValue}
            setFieldTouched={setFieldTouched}
            useAutocomplete={useSchoolExecutiveIdAutocomplete}
            assignedAgentsResponse={assignedAgentsResponse}
          />
        </div>
      </section>
      <section className={formStyles.section}>
        <h2 className={formStyles.sectionHeading}>Address</h2>
        <div className={formStyles.formGrid}>
          <FormField
            fullWidth
            error={errors.address}
            showErr={showError(touched.address, errors.address)}
          >
            <Textfield
              name="address"
              label="Address *"
              value={values.address}
              multiline
              minRows={3}
              handleOnChange={(e) => setAndTouch("address")(e.target.value)}
              onBlur={handleBlur}
            />
          </FormField>
          <FormField>
            {isCountryListLoading && countryOptions.length === 0 ? (
              <Loader />
            ) : (
              <SearchableSelect
                tag="Country *"
                options={countryOptions}
                value={selectedCountry ? values.countryId : ""}
                onBlur={touch("countryId")}
                onChange={handleCountryChange}
              />
            )}
            <SelectErrors
              state={selectErrors("countryId", isErrorOnCountryList)}
              yupMessage={errors.countryId}
              fetchMessage={fetchMessage(
                errorMessageOnFetchCountryList,
                "Failed to load countries",
              )}
            />
          </FormField>
          <FormField>
            {isCityListLoading ? (
              <Loader />
            ) : (
              <SearchableSelect
                tag="City *"
                options={cityOptions}
                value={values.cityId}
                disabled={!hasValidCountrySelected}
                onBlur={touch("cityId")}
                onChange={handleCityChange}
              />
            )}
            <SelectErrors
              state={selectErrors("cityId", isErrorOnCityList)}
              yupMessage={errors.cityId}
              fetchMessage={fetchMessage(
                errorMessageOnFetchCityList,
                "Failed to load cities",
              )}
            />
          </FormField>
          <FormField
            error={
              showError(touched.postalCode, errors.postalCode)
                ? errors.postalCode
                : showError(touched.postalCode, isInvalidPincode)
                  ? "Pincode is invalid"
                  : null
            }
            showErr={
              showError(touched.postalCode, errors.postalCode) ||
              showError(touched.postalCode, isInvalidPincode)
            }
          >
            <Textfield
              name="postalCode"
              label={isIndia ? "Postal code *" : "Postal code"}
              value={values.postalCode}
              handleOnChange={(event) => {
                const newValue = isIndia
                  ? event.target.value.replace(/\D/g, "").slice(0, INDIA_POSTAL_CODE_LENGTH)
                  : event.target.value;
                setAndTouch("postalCode")(newValue);
              }}
              onBlur={handleBlur}
            />
          </FormField>
          {STATE_DISTRICT_DISPLAY_FIELDS.map((field) => (
            <FormField
              key={field}
              error={errors[field]}
              showErr={showError(touched[field], errors[field])}
            >
              {isIndia && isPostalLookupPending ? (
                <Loader />
              ) : (
                <Textfield
                  name={field}
                  label={`${field.charAt(0).toUpperCase()}${field.slice(1)}${isIndia ? " *" : ""}`}
                  value={values[field]}
                  disabled={isStateDistrictReadOnly}
                  onBlur={handleBlur}
                />
              )}
            </FormField>
          ))}
          <FormField
            error={errors.area}
            showErr={showError(touched.area, errors.area)}
          >
            <Textfield
              name="area"
              label={isAreaRequired ? "Area *" : "Area"}
              value={values.area}
              disabled={isAreaDisabled}
              handleOnChange={(e) => setAndTouch("area")(e.target.value)}
              onBlur={handleBlur}
            />
          </FormField>
        </div>
      </section>
      <section className={formStyles.section}>
        <h2 className={formStyles.sectionHeading}>Academic details</h2>
        <div className={formStyles.formGrid}>
          {SCHOOL_BOARD_FIELDS.map(({ name, label }) => (
            <FormField key={name}>
              {isBoardListLoading ? (
                <Loader />
              ) : (
                <div className={styles.selectWrap}>
                  <MultipleSelect
                    name={name}
                    tag={`${label} *`}
                    maxVisibleChips={MAX_VISIBLE_BOARD_CHIPS}
                    onBlur={handleBlur(name)}
                    menuItems={boardOptions}
                    value={values[name]}
                    handleChange={(e) => setAndTouch(name)(e.target.value)}
                  />
                </div>
              )}
              <SelectErrors
                state={selectErrors(name, isErrorOnBoardList)}
                yupMessage={errors[name]}
                fetchMessage={fetchMessage(
                  errorMessageOnFetchBoardList,
                  "Failed to load boards",
                )}
              />
            </FormField>
          ))}
          <FormField>
            {isTierListLoading ? (
              <Loader />
            ) : (
              <SingleSelect
                name="tierId"
                tag="Tier *"
                menuItems={tierOptions}
                value={values.tierId}
                disabled
                onBlur={handleBlur("tierId")}
                handleChange={() => {}}
              />
            )}
            <SelectErrors
              state={selectErrors("tierId", isErrorOnTierList)}
              yupMessage={errors.tierId}
              fetchMessage={fetchMessage(
                errorMessageOnFetchTierList,
                "Failed to load tiers",
              )}
            />
          </FormField>
          <FormField
            error={errors.fee}
            showErr={showError(touched.fee, errors.fee)}
          >
            <Textfield
              name="fee"
              label="Annual School Fee *"
              type="number"
              value={values.fee}
              handleOnChange={(e) => setAndTouch("fee")(e.target.value)}
              onBlur={handleBlur}
            />
          </FormField>
          <FormField
            error={errors.totalStudentCount}
            showErr={showError(touched.totalStudentCount, errors.totalStudentCount)}
          >
            <Textfield
              name="totalStudentCount"
              label="Total student count *"
              type="number"
              value={values.totalStudentCount}
              handleOnChange={(e) => setAndTouch("totalStudentCount")(e.target.value)}
              onBlur={handleBlur}
            />
          </FormField>
          <FormField
            error={errors.totalTeacherCount}
            showErr={showError(touched.totalTeacherCount, errors.totalTeacherCount)}
          >
            <Textfield
              name="totalTeacherCount"
              label="Total teacher count"
              type="number"
              value={values.totalTeacherCount}
              handleOnChange={(e) => setAndTouch("totalTeacherCount")(e.target.value)}
              onBlur={handleBlur}
            />
          </FormField>
        </div>
      </section>
      <GradeWiseFields
        values={values}
        errors={errors}
        touched={touched}
        gradeOptions={gradeOptions}
        isGradeListLoading={isGradeListLoading}
        isErrorOnGradeList={isErrorOnGradeList}
        errorMessageOnFetchGradeList={errorMessageOnFetchGradeList}
        submitAttempted={submitAttempted}
        setFieldValue={setFieldValue}
        handleBlur={handleBlur}
      />
    </div>
  );
};

export default SchoolForm;
